import { Action, ConfirmModal, Icon, Menu } from 'corso-admin-ui';
import { isTruthy } from 'corso-types';
import { useState } from 'react';
import {
  useAutomationUpsert,
  useAutomations,
} from '~/features/settings/hooks/useAutomations';
import {
  useProtectionRateDelete,
  useProtectionRateUpsert,
} from '~/features/settings/hooks/useProtectionRates';
import { Automation, ProtectionRate } from '~/types';
import { ShippingRate } from './ShippingRate';
import ShippingRateForm from './ShippingRateForm';

function getInUseRateNames({
  rate,
  automations,
}: {
  rate: ProtectionRate;
  automations: Automation[];
}) {
  const quoteOrderShipmentRules = automations.filter(
    (rule) => rule.rule.event.type === `quoteOrderShipment`,
  );

  const modifyQuoteOrderShipmentRules = automations.filter(
    (rule) => rule.rule.event.type === `quoteOrderShipmentModify`,
  );

  const quoteOrderShipmentRulesInUse = quoteOrderShipmentRules
    .map(({ name, rule: { event } }) => {
      if (
        'storeShipRateIds' in event.params &&
        event.params.storeShipRateIds.includes(rate.id)
      ) {
        return name;
      }
      return null;
    })
    .filter(isTruthy);

  const modifyQuoteOrderShipmentInUse = modifyQuoteOrderShipmentRules
    .map(({ name, rule: { event } }) => {
      if ('checkoutRateModifications' in event.params) {
        const { checkoutRateModifications } = event.params;
        const modificationInUse = checkoutRateModifications
          .filter((mod) => mod.kind === 'ADD_OR_REMOVE_RATES')
          .find((mod) => mod.addOrRemoveStoreShipRateIds.includes(rate.id));

        return modificationInUse ? name : null;
      }
      return null;
    })
    .filter(isTruthy);

  return [
    ...modifyQuoteOrderShipmentInUse,
    ...quoteOrderShipmentRulesInUse,
  ].filter(isTruthy);
}

function ManageShippingRate({
  rate,
  storeRuleId,
  editable,
}: {
  rate: ProtectionRate;
  storeRuleId?: number;
  editable: boolean;
}) {
  const { mutate: deleteProtectionRate, isPending } = useProtectionRateDelete();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [ruleInUseConfirmation, setRuleInUseConfirmation] = useState(false);
  const [showEdt, setShowEdit] = useState(false);

  const { mutate: upsertRate } = useProtectionRateUpsert();
  const { mutate: upsertAutomation } = useAutomationUpsert();
  const { data: automations } = useAutomations();

  const inUseRuleNames = getInUseRateNames({
    rate,
    automations,
  });

  const handleUpdateRule = (
    rateId: number,
    ruleId: number,
    action: 'add' | 'remove',
  ) => {
    const automation = automations.find((rule) => rule.id === ruleId);
    if (!automation) return;

    const { rule } = automation;
    if (rule.event.type !== `quoteOrderShipment`) return;

    const storeShipRateIds =
      action === 'add'
        ? Array.from(new Set([...rule.event.params.storeShipRateIds, rateId]))
        : rule.event.params.storeShipRateIds.filter((id) => id !== rateId);

    upsertAutomation({
      ...automation,
      rule: {
        ...rule,
        event: {
          ...rule.event,
          params: {
            ...rule.event.params,
            storeShipRateIds,
          },
        },
      },
    });
  };

  const handleDuplicateRate = () => {
    const { id, ...newRate } = rate;
    upsertRate({
      ...newRate,
      name: `${rate.name} (Copy)`,
      ...(rate.plusName && { plusName: `${rate.plusName} (Copy)` }),
    });
  };

  const handleDeleteRate = () => {
    if (inUseRuleNames.length > 0) {
      setRuleInUseConfirmation(true);
    } else {
      setShowDeleteConfirmation(true);
    }
  };

  const shippingAutomations = automations.filter(
    (rule) => rule.rule.event.type === `quoteOrderShipment`,
  );

  const showAdd = shippingAutomations.length > 0 && !storeRuleId && editable;

  return (
    <ShippingRate rate={rate}>
      {storeRuleId ? (
        <>
          <Action
            variant="ghost"
            icon="edit"
            onClick={() => setShowEdit(true)}
            accessibilityLabel="Edit Rate"
          />
          <Action
            variant="ghost"
            icon="close"
            onClick={() => handleUpdateRule(rate.id, storeRuleId, 'remove')}
            accessibilityLabel="Remove from Rule"
          />
        </>
      ) : (
        <>
          {editable && (
            <Menu
              align="end"
              buttonAs={
                <Action
                  icon="overflowMenu"
                  accessibilityLabel={`Actions for ${rate.name} Rate`}
                  variant="ghost"
                />
              }
            >
              <Menu.ItemButton onClick={() => setShowEdit(true)}>
                <Icon icon="edit" />
                Edit
              </Menu.ItemButton>

              <Menu.ItemButton onClick={() => handleDuplicateRate()}>
                <Icon icon="duplicate" />
                Duplicate
              </Menu.ItemButton>

              <Menu.ItemButton onClick={() => handleDeleteRate()}>
                <Icon icon="remove" />
                Delete
              </Menu.ItemButton>
            </Menu>
          )}

          {showAdd && (
            <Menu
              align="end"
              buttonAs={
                <Action
                  size="lg"
                  variant="ghost"
                  icon="add"
                  accessibilityLabel="Add to Rule"
                />
              }
            >
              {shippingAutomations.map((rule) => {
                const isDisabled =
                  'storeShipRateIds' in rule.rule.event.params &&
                  rule.rule.event.params.storeShipRateIds.includes(rate.id);

                return (
                  <Menu.ItemButton
                    key={rule.id}
                    disabled={isDisabled}
                    onClick={() => handleUpdateRule(rate.id, rule.id, 'add')}
                  >
                    {isDisabled
                      ? `Added to ${rule.name}`
                      : `Add to ${rule.name}`}
                  </Menu.ItemButton>
                );
              })}
            </Menu>
          )}
        </>
      )}

      <ConfirmModal
        prompt="Are you sure you want to delete this rate? This action cannot be undone."
        title="Delete Rate"
        show={showDeleteConfirmation}
        loading={isPending}
        onConfirm={() => deleteProtectionRate(`${rate.id}`)}
        onCancel={() => setShowDeleteConfirmation(false)}
      />

      <ConfirmModal
        prompt={
          <div className="flex flex-col gap-2">
            <p>
              This rate is in use by the following rules, and cannot be deleted.
            </p>
            <ul>
              {inUseRuleNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        }
        title="Rate in Use"
        variant="caution"
        show={ruleInUseConfirmation}
        onCancel={() => setRuleInUseConfirmation(false)}
        onConfirm={() => setRuleInUseConfirmation(false)}
      />

      <ShippingRateForm
        show={showEdt}
        onClose={() => setShowEdit(false)}
        values={rate}
      />
    </ShippingRate>
  );
}

export default function ManageShippingRates({
  rates,
  storeRuleId,
  editable,
  hidden,
}: {
  rates: ProtectionRate[];
  storeRuleId?: number;
  editable: boolean;
  hidden?: boolean;
}) {
  if (hidden) return null;
  return (
    <ul className="flex flex-col justify-between gap-px bg-neutral-200">
      {rates.map((rate) => (
        <li key={rate.id} className="bg-white pt-2.5 pb-2">
          <ManageShippingRate
            rate={rate}
            storeRuleId={storeRuleId}
            editable={editable}
          />
        </li>
      ))}
    </ul>
  );
}
